import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader';

export default function Card3D({ frontImage, backImage }) {
  const mountRef = useRef(null);
  const groupRef = useRef(null);
  const cardRef = useRef(null);
  const animationRef = useRef(null);

  const [isBack, setIsBack] = useState(false);

  useEffect(() => {
    const container = mountRef.current;
    if (!container) return;

    /* SIZE */

    const width = container.clientWidth;
    const height = width * 0.45;

    /* SCENE */

    const scene = new THREE.Scene();

    /* CAMERA */

    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);

    const cardWidth = 3.5;
    const cardHeight = 2;

    const distance =
      Math.max(cardWidth, cardHeight) / Math.tan((camera.fov * Math.PI) / 360);

    camera.position.set(0, 0.15, distance * 0.75);

    /* RENDERER */

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
    });

    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(width, height);

    renderer.outputColorSpace = THREE.SRGBColorSpace;

    renderer.toneMapping = THREE.ACESFilmicToneMapping;

    /* 밝기 조정 핵심 */

    renderer.toneMappingExposure = 0.9;

    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    container.appendChild(renderer.domElement);

    /* HDR */

    new RGBELoader().load(
      'https://dl.polyhaven.org/file/ph-assets/HDRIs/hdr/1k/studio_small_09_1k.hdr',
      (texture) => {
        texture.mapping = THREE.EquirectangularReflectionMapping;

        scene.environment = texture;
      }
    );

    /* TEXTURES */

    const loader = new THREE.TextureLoader();

    const frontTexture = loader.load(frontImage);

    const backTexture = loader.load(backImage);

    const paperNormal = loader.load(
      'https://raw.githubusercontent.com/mrdoob/three.js/dev/examples/textures/terrain/grasslight-big-nm.jpg'
    );

    /* GEOMETRY */

    const geometry = new THREE.BoxGeometry(cardWidth, cardHeight, 0.02);

    /* MATERIAL — 반사 줄이고 종이 느낌 강화 */

    const baseMaterial = {
      roughness: 0.42,
      metalness: 0.03,
      normalMap: paperNormal,
      normalScale: new THREE.Vector2(0.08, 0.08),
    };

    const materials = [
      new THREE.MeshStandardMaterial({
        color: '#f0f0f0',
        roughness: 0.6,
      }),
      new THREE.MeshStandardMaterial({
        color: '#f0f0f0',
        roughness: 0.6,
      }),
      new THREE.MeshStandardMaterial({
        color: '#f0f0f0',
        roughness: 0.6,
      }),
      new THREE.MeshStandardMaterial({
        color: '#f0f0f0',
        roughness: 0.6,
      }),
      new THREE.MeshStandardMaterial({
        map: frontTexture,
        ...baseMaterial,
      }),
      new THREE.MeshStandardMaterial({
        map: backTexture,
        ...baseMaterial,
      }),
    ];

    const card = new THREE.Mesh(geometry, materials);

    card.castShadow = true;
    card.receiveShadow = true;

    cardRef.current = card;

    const group = new THREE.Group();

    group.add(card);

    groupRef.current = group;

    scene.add(group);

    /* LIGHT — 차분한 스튜디오 조명 */

    const ambient = new THREE.AmbientLight(0xffffff, 0.28);

    scene.add(ambient);

    const keyLight = new THREE.DirectionalLight(0xffffff, 1.6);

    keyLight.position.set(4, 5, 6);

    keyLight.castShadow = true;

    scene.add(keyLight);

    const fillLight = new THREE.DirectionalLight(0xffffff, 0.25);

    fillLight.position.set(-4, 2, 3);

    scene.add(fillLight);

    const rimLight = new THREE.DirectionalLight(0xffffff, 1.2);

    rimLight.position.set(0, -4, -6);

    scene.add(rimLight);

    /* MOUSE ROTATION */

    let mouseX = 0;
    let mouseY = 0;

    const handleMouseMove = (event) => {
      const rect = container.getBoundingClientRect();

      mouseX = ((event.clientX - rect.left) / rect.width - 0.5) * 2;

      mouseY = ((event.clientY - rect.top) / rect.height - 0.5) * 2;
    };

    container.addEventListener('mousemove', handleMouseMove);

    /* FLOATING ANIMATION */

    let time = 0;

    const animate = () => {
      requestAnimationFrame(animate);

      time += 0.01;

      if (cardRef.current) {
        cardRef.current.rotation.y = mouseX * 0.35;

        cardRef.current.rotation.x = -mouseY * 0.25;

        group.position.y = Math.sin(time) * 0.04;
      }

      renderer.render(scene, camera);
    };

    animate();

    /* RESIZE */

    const handleResize = () => {
      const newWidth = container.clientWidth;

      const newHeight = newWidth * 0.45;

      camera.aspect = newWidth / newHeight;

      camera.updateProjectionMatrix();

      renderer.setSize(newWidth, newHeight);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      container.removeEventListener('mousemove', handleMouseMove);

      window.removeEventListener('resize', handleResize);

      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }

      geometry.dispose();
      renderer.dispose();
    };
  }, [frontImage, backImage]);

  /* FLIP — 항상 정확히 0 / 180 */

  useEffect(() => {
    if (!groupRef.current) return;

    const group = groupRef.current;

    const target = isBack ? Math.PI : 0;

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    const animateFlip = () => {
      const current = group.rotation.y;

      const diff = target - current;

      if (Math.abs(diff) < 0.002) {
        group.rotation.y = target;
        return;
      }

      group.rotation.y += diff * 0.14;

      animationRef.current = requestAnimationFrame(animateFlip);
    };

    animateFlip();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isBack]);

  return (
    <div className='relative w-full text-center pb-14'>
      <div
        ref={mountRef}
        className='w-full mx-auto  rounded-xl bg-neutral-50'
      />

      {/* Front / Back UI */}

      <div
        className='absolute z-10 -translate-x-1/2  left-1/2 bottom-16'
      >
        <div
          className='flex items-center p-1 rounded-full shadow-lg  bg-white/85 backdrop-blur-md ring-1 ring-black/5'
        >
          <button
            onClick={() => setIsBack(false)}
            className={`
              px-4
              py-1.5
              text-xs
              font-medium
              rounded-full
              transition
              ${
                !isBack
                  ? 'bg-black text-white'
                  : 'text-neutral-500 hover:text-black'
              }
            `}
          >
            Front
          </button>

          <button
            onClick={() => setIsBack(true)}
            className={`
              px-4
              py-1.5
              text-xs
              font-medium
              rounded-full
              transition
              ${
                isBack
                  ? 'bg-black text-white'
                  : 'text-neutral-500 hover:text-black'
              }
            `}
          >
            Back
          </button>
        </div>
      </div>
    </div>
  );
}
